import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Loader2, AlertTriangle, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { invokeEdgeFunction } from '@/lib/invokeEdgeFunction';
import { formatTimeRange } from '@/lib/dates';
import { formatGameNumber } from '@/lib/gameNumber';

export interface Court { id: string; name: string; is_active?: boolean }
export interface Session {
  id: string; label?: string | null; session_date: string;
  start_time: string; end_time: string; is_active?: boolean;
}
export interface GameDetail {
  court_id?: string | null; session_id?: string | null;
  scheduled_date?: string | null; scheduled_start_time?: string | null;
  scheduled_end_time?: string | null; locked?: boolean; manually_adjusted?: boolean;
  game_type?: string | null; round_number?: number | null; game_number?: number | null;
}

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  eventId: string;
  game: { id: string; team_a_name?: string; team_b_name?: string } | null;
  detail: GameDetail | null;
  courts: Court[];
  sessions: Session[];
  onSaved: () => void;
}

const NONE = '__none__';

export default function EditGameModal({
  open, onOpenChange, eventId, game, detail, courts, sessions, onSaved,
}: Props) {
  const [courtId, setCourtId] = useState<string>(NONE);
  const [sessionId, setSessionId] = useState<string>(NONE);
  const [startTime, setStartTime] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [conflicts, setConflicts] = useState<{ type: string; message: string }[]>([]);
  const [courtSelectOpen, setCourtSelectOpen] = useState(false);
  const [sessionSelectOpen, setSessionSelectOpen] = useState(false);
  const refreshAfterClose = useRef(false);

  const setModalOpen = (nextOpen: boolean) => {
    if (!nextOpen) {
      setCourtSelectOpen(false);
      setSessionSelectOpen(false);
    }
    onOpenChange(nextOpen);
  };

  useEffect(() => {
    if (open) {
      setCourtId(detail?.court_id ?? NONE);
      setSessionId(detail?.session_id ?? NONE);
      setStartTime(detail?.scheduled_start_time ? detail.scheduled_start_time.slice(0, 5) : '');
      setConflicts([]);
    }
  }, [open, detail]);

  const activeCourts = useMemo(() => courts.filter((c) => c.is_active !== false), [courts]);
  const activeSessions = useMemo(() => sessions.filter((s) => s.is_active !== false), [sessions]);
  const selectedSession = activeSessions.find((s) => s.id === sessionId) || null;

  const fmtSession = (s: Session) =>
    `${s.label ? s.label + ' · ' : ''}${new Date(s.session_date + 'T12:00:00').toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })} ${formatTimeRange(s.start_time, s.end_time)}`;

  // A game placed by mistake must be removable without a database visit.
  const unplace = async () => {
    if (!game) return;
    setSaving(true);
    try {
      const { data: res, error } = await invokeEdgeFunction('rle-operator-schedule-manual', {
        body: { action: 'edit_game', event_id: eventId, game_id: game.id, unplace: true },
      });
      if (error || !res?.ok) {
        toast.error(error?.message || res?.error?.message || 'Could not take the game off the board.');
        return;
      }
      toast.success('Game taken off the board — it is back in the unplaced tray.');
      refreshAfterClose.current = true;
      setModalOpen(false);
    } finally {
      setSaving(false);
    }
  };

  const submit = async (override: boolean) => {
    if (!game) return;
    if (sessionId === NONE) { toast.error('Pick a time slot / session.'); return; }
    if (!startTime) { toast.error('Enter a start time.'); return; }
    setSaving(true);
    try {
      const { data: res, error } = await invokeEdgeFunction('rle-operator-schedule-manual', {
        body: {
          action: 'edit_game',
          event_id: eventId,
          game_id: game.id,
          court_id: courtId === NONE ? null : courtId,
          session_id: sessionId === NONE ? null : sessionId,
          scheduled_start_time: startTime,
          override,
        },
      });
      if (error || !res?.ok) {
        toast.error(error?.message || res?.error?.message || 'Could not update the game.');
        return;
      }
      if (!res.data.applied) {
        setConflicts(res.data.conflicts || []);
        return;
      }
      toast.success('Game updated.');
      refreshAfterClose.current = true;
      setModalOpen(false);
    } finally {
      setSaving(false);
    }
  };


  return (
    <Dialog open={open} onOpenChange={setModalOpen}>
      <DialogContent
        onCloseAutoFocus={() => {
          if (!refreshAfterClose.current) return;
          refreshAfterClose.current = false;
          onSaved();
        }}
      >
        <DialogHeader>
          <DialogTitle>{formatGameNumber(detail ?? {}) || 'Edit game'}</DialogTitle>
          <DialogDescription asChild>
            <div className="space-y-1">
              <div>
                {game?.team_a_name} vs {game?.team_b_name}
                {detail?.locked && (
                  <span className="ml-2 inline-flex items-center text-amber-600">
                    <Lock className="h-3 w-3 mr-1" /> Locked
                  </span>
                )}
              </div>
              {/* A team's whole weekend, one click from the game in front of you. */}
              <div className="flex flex-wrap gap-3 text-xs">
                {[
                  { name: game?.team_a_name, reg: (detail as any)?.team_a_registration_id },
                  { name: game?.team_b_name, reg: (detail as any)?.team_b_registration_id },
                ].filter((t) => t.reg && t.name).map((t) => (
                  <a
                    key={String(t.reg)}
                    className="underline underline-offset-2"
                    href={`/admin-dashboard/events/${eventId}/scheduling/team-summary?team=${t.reg}`}
                  >
                    {t.name}'s weekend
                  </a>
                ))}
              </div>
            </div>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Court</Label>
            <Select open={courtSelectOpen} onOpenChange={setCourtSelectOpen} value={courtId} onValueChange={(v) => { setCourtId(v); setConflicts([]); }}>
              <SelectTrigger><SelectValue placeholder="Select court" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>No court</SelectItem>
                {activeCourts.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label>Time slot / session</Label>
            <Select open={sessionSelectOpen} onOpenChange={setSessionSelectOpen} value={sessionId} onValueChange={(v) => { setSessionId(v); setConflicts([]); }}>
              <SelectTrigger><SelectValue placeholder="Select session" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>No session</SelectItem>
                {activeSessions.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{fmtSession(s)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label>Start time</Label>
            <Input
              type="time"
              value={startTime}
              onChange={(e) => { setStartTime(e.target.value); setConflicts([]); }}
            />
            {selectedSession && (
              <p className="text-xs text-muted-foreground">
                Session runs {formatTimeRange(selectedSession.start_time, selectedSession.end_time)}.
              </p>
            )}
          </div>

          {conflicts.length > 0 && (
            <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm">
              <div className="flex items-center gap-2 font-medium text-amber-800 mb-1">
                <AlertTriangle className="h-4 w-4" /> Scheduling conflict
              </div>
              <ul className="list-disc pl-5 text-amber-800 space-y-0.5">
                {conflicts.map((c, i) => <li key={i}>{c.message}</li>)}
              </ul>
              <p className="text-xs text-amber-700 mt-2">
                You can still save this — it will be flagged as a manual override.
              </p>
            </div>
          )}
        </div>

        <DialogFooter className="sm:justify-between">
          {detail?.court_id || detail?.scheduled_start_time ? (
            <Button variant="ghost" className="text-destructive" onClick={() => void unplace()} disabled={saving || !!detail?.locked}>
              Take off the board
            </Button>
          ) : <span />}
          <div className="flex gap-2">
          <Button variant="outline" onClick={() => setModalOpen(false)} disabled={saving}>Cancel</Button>

          {conflicts.length > 0 ? (
            <Button variant="destructive" onClick={() => submit(true)} disabled={saving}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Override &amp; save
            </Button>
          ) : (
            <Button onClick={() => submit(false)} disabled={saving}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save
            </Button>
          )}
          </div>
        </DialogFooter>

      </DialogContent>
    </Dialog>
  );
}
